import * as bus from './localBus'
import { CONNECTION_STATE, CONNECTION_STATES } from './socketEvents'
import { createSocketWorker } from './socketWorkerFactory'

import * as Sentry from '@sentry/browser'

const supportsSharedWorker = typeof SharedWorker !== 'undefined'

const reportError = (msg, err) => {
  // eslint-disable-next-line no-console
  console.error(msg, err)
  if (typeof window !== 'undefined' && window.SENTRY_INITIALIZED) {
    Sentry.captureException(err, { extra: { context: msg }, tags: { subsystem: 'socket' } })
  }
}

const TAB_ID =
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

// ---------- SharedWorker transport ----------

let worker = null
let port = null
let lifecycleAbort = null
const pendingAcks = new Map()
let connectionState = CONNECTION_STATES.DISCONNECTED
// Whether the app currently wants a live socket — set on connect(), cleared on
// disconnect(). Read by demoteToFallback() to decide if the fallback transport
// should immediately reconnect after a late SharedWorker failure.
let connectRequested = false

const handlePortMessage = (msg) => {
  if (!msg) return
  switch (msg.type) {
    case 'socket-event':
      bus.emit(msg.event, msg.payload)
      break
    case 'connection-state': {
      const next = msg.state || CONNECTION_STATES.DISCONNECTED
      const wasConnected = connectionState === CONNECTION_STATES.CONNECTED
      connectionState = next
      bus.emit(CONNECTION_STATE, next)
      if (wasConnected && next !== CONNECTION_STATES.CONNECTED) {
        pendingAcks.forEach(({ reject }) => reject(new Error('socket disconnected')))
        pendingAcks.clear()
      }
      break
    }
    case 'emit-ack-response': {
      const resolver = pendingAcks.get(msg.requestId)
      if (resolver) {
        pendingAcks.delete(msg.requestId)
        resolver.resolve(msg.response)
      }
      break
    }
    default:
      break
  }
}

const ensureWorker = () => {
  if (port) return port
  worker = createSocketWorker()
  // A constructor that succeeds can still fail asynchronously: the worker
  // script loads in the background, and a load failure (e.g. the hashed chunk
  // 404ing through the nginx fallback during a deploy-skew window — IC-50708)
  // fires `error` on the worker instead of throwing. Without a handler the
  // transport stays locked on a dead port and the tab silently never gets a
  // socket.
  // eslint-disable-next-line no-use-before-define
  worker.onerror = (event) => demoteToFallback('worker script failed to load', event)
  port = worker.port
  port.onmessage = (e) => handlePortMessage(e.data)
  // eslint-disable-next-line no-use-before-define
  port.onmessageerror = (event) => demoteToFallback('port message failed to deserialize', event)
  port.start()
  return port
}

const sharedWorkerSupported = () => {
  if (!supportsSharedWorker) return false
  try {
    ensureWorker()
    return true
  } catch (err) {
    worker = null
    port = null
    // SharedWorker requires the worker script to be EXACT same-origin with the
    // page (scheme+host+port) — CORS doesn't apply and no constructor option
    // relaxes it. When bundles are served from a CDN origin (independent FE
    // CI/CD, e.g. *.pages.dev behind members.*), construction deterministically
    // throws SecurityError on every page load — expected, so demote quietly
    // instead of reporting to Sentry. Goes away once the worker chunk resolves
    // same-origin (rspack output.workerPublicPath + the members.* nginx
    // 404-fallback proxy to the FE host).
    if (err?.name === 'SecurityError') {
      // eslint-disable-next-line no-console
      console.info(
        '[socket] worker script is cross-origin to the page — SharedWorker cannot be constructed; using per-tab socket.'
      )
      return false
    }
    // Anything else is unexpected — e.g. module workers unsupported, or an
    // enterprise CSP blocked it. Report and demote to fallback.
    reportError('[socket] SharedWorker construction failed, demoting to fallback', err)
    return false
  }
}

// Wipe all references to the SharedWorker port without aborting the lifecycle
// listeners — used when the page is suspended into BFCache (we want pageshow
// to still fire on the same handler that's preserved with the document).
const wipePortState = () => {
  port = null
  worker = null
  connectionState = CONNECTION_STATES.DISCONNECTED
  pendingAcks.forEach(({ reject }) => reject(new Error('socket suspended')))
  pendingAcks.clear()
}

const bindLifecycleListeners = () => {
  if (lifecycleAbort || typeof window === 'undefined') return
  lifecycleAbort = new AbortController()
  const { signal } = lifecycleAbort

  const sendDisconnect = () => {
    if (!port) return
    try {
      port.postMessage({ type: 'disconnect', tabId: TAB_ID })
    } catch (e) {
      // worker may already be gone
    }
  }

  const reconnectIfDown = () => {
    if (port && connectionState !== CONNECTION_STATES.CONNECTED) {
      port.postMessage({ type: 'connect', origin: window.location.origin, tabId: TAB_ID })
    }
  }

  // pagehide fires on real unload AND when the page enters BFCache. The
  // event.persisted flag tells us which: true = BFCache, false = real unload.
  // For BFCache we must wipe local port/state too, otherwise on pageshow the
  // restored module thinks it's still connected (stale `connectionState`) and
  // never reconnects to the worker — which already removed our port.
  // We deliberately do NOT abort lifecycleAbort here, so the pageshow handler
  // below remains attached to the BFCache-restored document.
  const handlePageHide = (event) => {
    sendDisconnect()
    if (event?.persisted === true) {
      // Defer so the disconnect postMessage flushes before the port is detached.
      queueMicrotask(() => {
        if (port) {
          try {
            port.close()
          } catch (e) {
            /* ignore */
          }
        }
        wipePortState()
      })
    }
  }

  // BFCache restore: re-handshake with a fresh worker port. ensureWorker()
  // will rebuild because wipePortState() set port=null. lifecycleAbort is
  // still set so bindLifecycleListeners() inside connect() is a no-op.
  const handlePageShow = (event) => {
    if (event?.persisted === true) {
      sharedWorkerTransport.connect()
    }
  }

  // Note: beforeunload is intentionally omitted. In modern Chromium/Firefox
  // adding a beforeunload handler disqualifies the page from BFCache, defeating
  // fix above. pagehide covers both real unloads and BFCache transitions.
  window.addEventListener('pagehide', handlePageHide, { signal })
  window.addEventListener('pageshow', handlePageShow, { signal })
  window.addEventListener('online', reconnectIfDown, { signal })
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'visible') reconnectIfDown()
    },
    { signal }
  )
}

const sharedWorkerTransport = {
  connect: () => {
    connectRequested = true
    const p = ensureWorker()
    p.postMessage({ type: 'connect', origin: window.location.origin, tabId: TAB_ID })
    bindLifecycleListeners()
  },
  disconnect: () => {
    connectRequested = false
    if (!port) return
    const closingPort = port
    try {
      closingPort.postMessage({ type: 'disconnect', tabId: TAB_ID })
      // Defer close so the postMessage is observably queued before the port
      // is detached — the worker has no onclose for ports, so its only
      // signal that the tab is gone is the disconnect message.
      queueMicrotask(() => {
        try {
          closingPort.close()
        } catch (e) {
          // ignore
        }
      })
    } catch (e) {
      // ignore
    }
    port = null
    worker = null
    connectionState = CONNECTION_STATES.DISCONNECTED
    pendingAcks.forEach(({ reject }) => reject(new Error('socket disconnected')))
    pendingAcks.clear()
  },
  reset: () => {
    sharedWorkerTransport.disconnect()
    if (lifecycleAbort) {
      lifecycleAbort.abort()
      lifecycleAbort = null
    }
  },
  resetUnderlyingSocket: () => {
    const p = ensureWorker()
    pendingAcks.forEach(({ reject }) => reject(new Error('socket reset')))
    pendingAcks.clear()
    p.postMessage({ type: 'reset-socket', origin: window.location.origin, tabId: TAB_ID })
  },
  emit: (event, payload) => {
    ensureWorker().postMessage({ type: 'emit', event, payload })
  },
  emitWithAck: (event, payload, timeoutMs) =>
    new Promise((resolve, reject) => {
      const requestId = `${TAB_ID}-${Date.now()}-${Math.random().toString(36).slice(2)}`
      // Client-side safety timer with a small grace period over the worker's
      // socket.io timeout — the worker should always respond first (success or
      // error), but if the worker dies entirely we still reject.
      const timer = setTimeout(() => {
        pendingAcks.delete(requestId)
        reject(new Error(`socket ack timeout: ${event}`))
      }, timeoutMs + 1000)
      pendingAcks.set(requestId, {
        resolve: (response) => {
          clearTimeout(timer)
          if (response && response.__error) {
            reject(new Error(response.__error))
            return
          }
          resolve(response)
        },
        reject: (err) => {
          clearTimeout(timer)
          reject(err)
        },
      })
      ensureWorker().postMessage({ type: 'emit-ack', event, payload, requestId, timeoutMs })
    }),
}

// ---------- Direct fallback transport (lazy) ----------

let fallbackTransport = null
let fallbackLoading = null

const loadFallback = () => {
  if (fallbackTransport) return Promise.resolve(fallbackTransport)
  if (fallbackLoading) return fallbackLoading
  fallbackLoading = import('./socketDirectFallback').then((mod) => {
    fallbackTransport = mod
    return mod
  })
  return fallbackLoading
}

const safeLoad = () =>
  loadFallback().catch((err) => {
    reportError('[socket] failed to load direct fallback chunk', err)
    fallbackLoading = null // allow retry on next call
    throw err
  })

const fallbackProxy = {
  connect: () => {
    safeLoad()
      .then((m) => m.connect(window.location.origin))
      .catch(() => {})
  },
  disconnect: () => {
    if (fallbackTransport) fallbackTransport.disconnect()
  },
  reset: () => {
    if (fallbackTransport) fallbackTransport.reset()
  },
  resetUnderlyingSocket: () => {
    safeLoad()
      .then((m) => {
        m.reset()
        m.connect(window.location.origin)
      })
      .catch(() => {})
  },
  emit: (event, payload) => {
    if (fallbackTransport) {
      fallbackTransport.emit(event, payload)
    } else {
      safeLoad()
        .then((m) => m.emit(event, payload))
        .catch(() => {})
    }
  },
  emitWithAck: (event, payload, timeoutMs) => {
    if (fallbackTransport) return fallbackTransport.emitWithAck(event, payload, timeoutMs)
    return safeLoad().then((m) => m.emitWithAck(event, payload, timeoutMs))
  },
}

// ---------- Dispatcher ----------

let transport = null

// Late SharedWorker failure (after construction succeeded and `transport` was
// already locked in): tear the worker path down and swap the cached transport
// to the per-tab fallback, mirroring what the synchronous SecurityError path
// does. Unlike that path this is unexpected, so it does report to Sentry.
const demoteToFallback = (reason, event) => {
  if (transport === fallbackProxy) return
  reportError(
    `[socket] SharedWorker failed after construction — demoting to per-tab socket (${reason})`,
    new Error(event?.message || reason)
  )
  const shouldReconnect = connectRequested
  // reset() closes the dead port, rejects pending acks, and aborts the
  // lifecycle listeners — so a BFCache pageshow can't resurrect the worker.
  sharedWorkerTransport.reset()
  transport = fallbackProxy
  if (shouldReconnect) fallbackProxy.connect()
}

const getTransport = () => {
  if (transport) return transport
  if (sharedWorkerSupported()) {
    transport = sharedWorkerTransport
  } else {
    if (supportsSharedWorker === false) {
      // eslint-disable-next-line no-console
      console.warn(
        '[socket] SharedWorker unavailable — falling back to per-tab socket. ' +
          'Multiple tabs will open separate connections.'
      )
    }
    transport = fallbackProxy
  }
  return transport
}

export const connectSocket = () => getTransport().connect()

export const disconnectSocket = () => getTransport().disconnect()

export const resetSocket = () => getTransport().reset()

export const reconnectSocketForTenantSwitch = () => getTransport().resetUnderlyingSocket()

export const internalEmit = (event, payload) => getTransport().emit(event, payload)

// Promise.resolve(thenable) adopts the thenable's state, so this works whether
// transport.emitWithAck returns a Promise<Response> (SharedWorker path) or a
// Promise<Promise<Response>> (fallback path that defers through dynamic import).
export const internalEmitWithAck = (event, payload, timeoutMs) =>
  Promise.resolve(getTransport().emitWithAck(event, payload, timeoutMs))
